import { Group, Stack } from '@mantine/core';
import type { LiteIntegration, LiteWorkspace } from '@repo/api-codegen';
import { useApiSuperuserGetIntegration } from '@repo/api-codegen';
import { Button, Icon, ListBox, Text } from '@repo/foundations';
import { useState } from 'react';
import IntegrationLogo from '../../../components/IntegrationLogo';

interface SuperuserIntegrationSelectorProps {
	workspace?: LiteWorkspace;
	selected?: LiteIntegration;
	setSelected: (integration?: LiteIntegration) => void;
}

export function SuperuserIntegrationSelector({
	workspace,
	selected,
	setSelected,
}: SuperuserIntegrationSelectorProps) {
	const [opened, setOpened] = useState(false);
	const [searchTerm, setSearchTerm] = useState('');
	const { data: integrations } = useApiSuperuserGetIntegration(
		{
			queryParams: {
				search_term: searchTerm,
				...(workspace ? { workspace_id: workspace.id } : {}),
			},
		},
		{
			select: (data) => {
				if (selected) {
					return [selected, ...data.filter((i) => i.id !== selected.id)];
				}
				return data;
			},
		}
	);

	return (
		<ListBox
			opened={opened}
			onOpenChange={(newOpened) => {
				setSearchTerm('');
				setOpened(newOpened);
			}}
			closeOnEscape
			placement="bottom-start"
		>
			<ListBox.Target>
				<Button onClick={() => setOpened(!opened)} h={35}>
					{selected && selected.type ? (
						<Group spacing="sm">
							<IntegrationLogo integrationType={selected.type} size={20} />
							{selected.name}
						</Group>
					) : (
						'Choose integration'
					)}
				</Button>
			</ListBox.Target>
			<ListBox.ItemsDropdown
				usePortal
				search={{
					onChange: setSearchTerm,
					value: searchTerm,
					placeholder: 'Search integration...',
				}}
				items={integrations}
				renderItem={(item) => (
					<ListBox.Item
						rightSection={selected?.id === item?.id && <Icon name="check" />}
						key={item.id}
						onClick={() => {
							setSelected(selected?.id === item?.id ? undefined : item);
							setOpened(false);
						}}
					>
						<Group spacing="sm">
							<IntegrationLogo
								integrationType={item.type || undefined}
								size={20}
							/>
							<Stack spacing={0}>
								<Text size="sm" weight="bold">
									{' '}
									{item.name}
								</Text>
								<Text size="sm">{item.id}</Text>
							</Stack>
						</Group>
					</ListBox.Item>
				)}
			/>
		</ListBox>
	);
}
